import React, {FC, useState, useEffect} from "react";
import { useQueryParam, StringParam, NumberParam } from 'use-query-params';
import { Button, ModalVariables, StartPage, Typography } from "../../../../../../components";
import { billingService } from "../../../../../../api";
import { timeConverterMMDDYYHHMM } from "../../../../../../utils/dates";
import { useTranslation } from "react-i18next";
import { formatCurrency } from "../../../../../../utils/display-price";
import { icons } from "../../../../../../icons";
import { useNavigate, useParams } from "react-router";
import { useUi } from "../../../../../../context/ui.context";
import { compareArrays } from "../../../../../../utils/findIntersection";

const Journal:FC = () => {
  const [t] = useTranslation()
  const navigate = useNavigate()
  const [page, setPage] = useQueryParam('page', NumberParam);
  const [ordering, setOrdering] = useQueryParam('ordering', StringParam);
  const [pageSize, setPageSize] = useQueryParam('page_size', NumberParam);
  const [searchString, setSearchString] = useQueryParam('search', StringParam);
  const [paginationData, setPaginationData] = useState<any>()
  const [invoices, setInvoices] = useState<any[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [pageLoading, setPageLoading] = useState<boolean>(true)
  const {
    viewAlert,
  } = useUi()
  const { userId } = useParams();

  const [modalType, setModalType] = useState<any>('')
  const [alertTitle, setAlertTitle] = useState<any>('')
  const [alertSubTitle, setAlertSubTitle] = useState<any>()
  const [viewModal, setViewModal] = useState<boolean>(false)
  const [activeAnimation, setActiveAnimation] = useState<boolean>(false)
  const [selectedItem, setSelectedItem] = useState<any>(null)
  const [allChecked, setAllChecked] = useState<boolean>(false)
  const [checked, setChecked] = useState<any[]>([])
  const formatCreditLeft = (invoice:any) => {
    if(invoice.client_credit_left === null || invoice.client_credit_left === undefined || !invoice.client_credit_left_currency){
      return ''
    }

    return formatCurrency(invoice.client_credit_left, invoice.client_credit_left_currency)
  }
  const editChecked = (id:any, isAll?:boolean) => {
    if(id === null) {
      setChecked([])
      setAllChecked(false)
      return
    }
    const founded = checked.find((item:any) => item.id === id?.id)
    if(founded) {
      if(isAll){ 
        return 
      } else {
        setChecked((prev:any) => {
          return prev.filter((item:any) => item.id !== id?.id)
        })
        setAllChecked(false)
      }
    } else {
      setChecked((prev) => [...prev, id])
    }
  } 
  useEffect(() => {
    if(compareArrays(invoices, checked) && checked?.length !== 0){
      setAllChecked(true)
    }
  },[checked])
  
  const getInvoices = () => {
    billingService.getJournal({
      page:page || 1,
      page_size:pageSize || 20,
      search:searchString||'',
      client:userId
    })
    .then((res) => {
      setInvoices(res.data.objects.map((invoice:any) => {
        return {
          ...invoice,
          name: timeConverterMMDDYYHHMM(invoice.date_added),
          navigate:`/billing/journal/${invoice.id}`,
          total:formatCurrency(invoice.destination_amount, invoice.destination_currency),
          credit_left:formatCreditLeft(invoice),
          source_info:(
            <div style={{
              display:'flex',
              flexDirection:'column',
              gap:'2px',
            }}>
             <Typography variant="body">
              {invoice?.client?.name}
             </Typography>
             <Typography  variant="body" customStyles={{opacity:'0.5'}}>
              {invoice?.source_info?.name}
             </Typography>
            </div>
          ),
          destination_info:(
            <div style={{
              display:'flex',
              flexDirection:'column',
              gap:'4px',
            }}>
             <Typography variant="body">
              {invoice?.destination_info?.name}
             </Typography>
            </div>
          ),
        }
      }))
      setPaginationData(res.data)
      setPageLoading(false)
    })
    .catch((e) => {
      console.log('error', e);
      setPageLoading(false)
    })
  }
  useEffect(() => {
    getInvoices()
    const interval = setInterval(() => {
      getInvoices()
    }, 5000)
    return () => {
      clearInterval(interval)
    }
  },[page,
    ordering,
    pageSize,
    searchString])
  const ColumnsInvoices = [
    {
      title:t("DATE"),
      key:'name'
    },
    {
      title:t("SOURCE_INFO"),
      key:'source_info',
      // sorted:true,
    },
    {
      title:t("DESTINATION_INFO"),
      // sorted:true,
      key:'destination_info'
    },
    {
      title:t("PREPAID_CREDIT_BALANCE"),
      key:'credit_left'
    },
    {
      title:t("AMOUNT"),
      key:'total'
    },
    {
      title:'',
      key:'props'
    }
  ]

  const closeModalCancel = () => {
    setViewModal(false)
    setActiveAnimation(false)
    setSelectedItem(null)
  }
  const openModal = (modal:any, options?:any) => {
    setModalType(modal)
    setAlertTitle(options?.alertTitle || '')
    setAlertSubTitle(options?.alertSubTitle)
    setViewModal(true)
    setActiveAnimation(true)
  }
  const closeModal = async (values:any) => {
    setLoading(true);
    try {
      if (selectedItem) {
        await billingService.deleteJournalItem(selectedItem.id);
        getInvoices();
        setChecked([]);
        setAllChecked(false);
      } else if (allChecked) {
        await Promise.all(invoices.map((item:any) => billingService.deleteJournalItem(item.id)));
        getInvoices();
        setAllChecked(false);
        setChecked([]);
      } else if (checked?.length > 0) {
        await Promise.all(checked.map((item) => billingService.deleteJournalItem(item.id)));
        getInvoices();
        setChecked([]);
      }
    } catch (e:any) {
      if (e?.response?.data.detail) {
        viewAlert({
          severity: 'error',
          message: e.response.data.detail,
        });
      }
      setChecked([]);
    } finally {
      setViewModal(false);
      setActiveAnimation(false);
      setSelectedItem(null);
      setLoading(false);
    }
  } 

  const contextMenu = [
    {
      title:t("EDIT"),
      id:'3',
      icon:icons.Edit,
      onClick: (item:any) => {
        navigate(`${item.id}/edit`)
      },
      divider:false
    },
    {
      title:t("DELETE"),
      id:'4',
      onClick:(item:any) => {
        openModal('delete', {
          alertSubTitle:[item],
          alertTitle:t("RECORD"),
          closeModal
        })
        setSelectedItem(item)
      },
      icon:icons.DeleteIcon,
      divider:false
    },
  ]
  
  return (
    <>
    {/* @ts-ignore */}
      <StartPage
        title={`${t("JOURNAL")}`}
        noResourceText={`${t("NO_JOURNAL_RECORDS")}`}
        haveResource={invoices.length > 0}
        pageLoading={pageLoading}
        withoutCreateResourceButton
        rightButtonComponentCustomWithResources={
          <div style={{display:'flex', alignItems:'center'}}>
            <Button
              onClick={() => {
                openModal('delete', {
                  alertSubTitle:allChecked ? invoices : checked,
                  alertTitle:t("JOURNAL"),
                  closeModal
                })
              }}
              variant='icon'
              size='display'
              test_id='button-delete'
              disabled={!allChecked && checked?.length === 0 }
              customStyles={{margin:'0px 6px 0px 0px'}}
            >
              <img src={icons.DeleteIcon}/>
            </Button>
          </div>
        }
        tableData={{
          columns:ColumnsInvoices,
          data:invoices,
          props:contextMenu,
          paginationData:paginationData,
          updateData:getInvoices,
          allChecked:allChecked,
          setAllChecked:setAllChecked,
          checked:checked,
          setChecked:editChecked
        }}
      />
      <ModalVariables
        modalType={modalType}
        viewModal={viewModal}
        activeAnimation={activeAnimation}
        closeModal={closeModal}
        closeModalCancel={closeModalCancel}
        alertTitle={alertTitle}
        alertSubTitle={alertSubTitle}
        loading={loading}
        instance={selectedItem}
      />
    </>
  )
}

export default Journal
